import { useState, useEffect, useCallback } from 'react';
import { groupAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { UsersRound, Plus, Send, UserPlus, MessageCircle, FileText, Loader2 } from 'lucide-react';

function CreateGroupForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [error, setError] = useState('');

  const handle = async () => {
    if (!name.trim()) return;
    try {
      const r = await groupAPI.create(name.trim(), desc.trim());
      onCreated(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="border border-[#2F8A92]/20 rounded p-3 space-y-2" data-testid="create-group-form">
      <input
        data-testid="group-name-input"
        className="hud-input text-sm"
        placeholder="Group name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        data-testid="group-desc-input"
        className="hud-input text-sm"
        placeholder="What are you researching? (optional)"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      {error && <p className="text-[10px] text-red-400 font-mono">{error}</p>}
      <div className="flex gap-2">
        <button
          data-testid="save-group-btn"
          onClick={handle}
          disabled={!name.trim()}
          className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-3 py-1.5 hover:bg-[#66FAFF] disabled:opacity-40"
        >
          CREATE
        </button>
        <button
          onClick={onCancel}
          className="font-hud text-[10px] text-white/80 tracking-wider hover:text-[#2F8A92]"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

function InviteToGroup({ groupId, currentCount, onInvited }) {
  const [emails, setEmails] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return;
    setBusy(true);
    try {
      const r = await groupAPI.invite(groupId, list);
      setResult(r.data);
      setEmails('');
      onInvited?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid="invite-group-members">
      <input
        data-testid="group-invite-emails"
        className="hud-input text-xs flex-1"
        placeholder="Invite by email…"
        value={emails}
        onChange={(e) => setEmails(e.target.value)}
      />
      <button
        data-testid="group-invite-btn"
        onClick={handle}
        disabled={busy}
        className="font-hud text-[10px] tracking-wider text-[#2F8A92] border border-[#2F8A92]/30 px-3 py-1.5 hover:bg-[#2F8A92]/10 disabled:opacity-40 flex items-center gap-1"
      >
        <UserPlus size={10} /> INVITE
      </button>
      {result && (
        <span className="font-mono text-[10px] text-white/80">+{result.added?.length || 0} · {result.total}/12</span>
      )}
    </div>
  );
}

function GroupDetail({ group, onBack, onRefresh }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newComment, setNewComment] = useState('');

  const load = useCallback(() => {
    groupAPI.listComments(group.id).then((r) => setComments(r.data)).catch(() => {});
    groupAPI.listNotes(group.id).then((r) => setNotes(r.data)).catch(() => {});
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    await groupAPI.postComment(group.id, newComment.trim());
    setNewComment('');
    load();
  };

  return (
    <div className="space-y-4" data-testid="group-detail">
      <div className="flex items-center gap-3">
        <button
          data-testid="group-back"
          onClick={onBack}
          className="font-hud text-[10px] tracking-wider text-[#52525B] hover:text-[#2F8A92]"
        >
          ← BACK
        </button>
        <div>
          <h3 className="font-historical text-[#D4A84A] text-lg">{group.name}</h3>
          <p className="font-mono text-[10px] text-[#52525B]">
            {(group.member_ids || []).length}/12 members · {group.description}
          </p>
        </div>
      </div>

      <InviteToGroup
        groupId={group.id}
        currentCount={(group.member_ids || []).length}
        onInvited={() => { onRefresh?.(); load(); }}
      />

      {/* Shared notes */}
      <div>
        <h5 className="font-hud text-[10px] tracking-widest uppercase text-[#2F8A92] mb-2 flex items-center gap-1">
          <FileText size={11} /> Shared Notes ({notes.length})
        </h5>
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="border border-[#2F8A92]/10 rounded p-3" data-testid={`group-note-${n.id}`}>
              <p className="font-historical text-sm text-[#D4A84A]">{n.title}</p>
              <p className="font-mono text-[10px] text-[#52525B] mt-0.5">
                Shared by {n.shared_by_name} · {new Date(n.created_at).toLocaleString()}
              </p>
              <div className="mt-2 space-y-2">
                {(n.sections || []).map((s) => (
                  <div key={s.id} className="text-[11px] font-mono">
                    {s.heading && <p className="text-[#e8d5a3] font-historical text-xs">{s.heading}</p>}
                    <p className="text-white/80 line-clamp-3">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {notes.length === 0 && (
            <p className="font-mono text-[10px] text-[#52525B]">
              No notes shared yet. Open the Notepad tab, then use Export → this group.
            </p>
          )}
        </div>
      </div>

      {/* Comments */}
      <div>
        <h5 className="font-hud text-[10px] tracking-widest uppercase text-[#2F8A92] mb-2 flex items-center gap-1">
          <MessageCircle size={11} /> Comments ({comments.length})
        </h5>
        <div className="space-y-2 mb-3">
          {comments.map((c) => (
            <div key={c.id} className="border-l-2 border-[#2F8A92]/20 pl-3 py-1" data-testid={`group-comment-${c.id}`}>
              <p className="font-hud text-[10px] tracking-wider text-[#2F8A92]">
                {c.user_name} <span className="text-[#52525B]">· {new Date(c.created_at).toLocaleString()}</span>
              </p>
              <p className="font-mono text-[11px] text-white/80 whitespace-pre-wrap">{c.content}</p>
            </div>
          ))}
          {comments.length === 0 && <p className="font-mono text-[10px] text-[#52525B]">No comments yet.</p>}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="new-comment-input"
            className="hud-input text-xs flex-1"
            placeholder="Share an observation…"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && postComment()}
          />
          <button
            data-testid="post-comment-btn"
            onClick={postComment}
            disabled={!newComment.trim()}
            className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-3 py-1.5 hover:bg-[#66FAFF] disabled:opacity-40 flex items-center gap-1"
          >
            <Send size={10} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    groupAPI.list()
      .then((r) => setGroups(r.data))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse p-4">LOADING GROUPS…</div>;
  }

  if (selected) {
    return <GroupDetail group={selected} onBack={() => { setSelected(null); load(); }} onRefresh={load} />;
  }

  return (
    <div className="space-y-4" data-testid="groups-mode-b">
      <div className="flex items-center justify-between">
        <h3 className="font-hud text-[#2F8A92] text-sm tracking-widest uppercase flex items-center gap-2">
          <UsersRound size={14} /> PEER GROUPS
        </h3>
        {!creating && (
          <button
            data-testid="new-group-btn"
            onClick={() => setCreating(true)}
            className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-3 py-1.5 hover:bg-[#66FAFF] flex items-center gap-1"
          >
            <Plus size={10} /> NEW GROUP
          </button>
        )}
      </div>

      {creating && (
        <CreateGroupForm onCreated={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />
      )}

      {groups.length === 0 && !creating ? (
        <p className="font-mono text-[11px] text-[#52525B]">
          No groups yet. Groups hold up to 12 members. Any paid user can create one.
        </p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => (
            <button
              key={g.id}
              data-testid={`group-${g.id}`}
              onClick={() => setSelected(g)}
              className="w-full text-left border border-[#2F8A92]/10 rounded p-3 hover:border-[#2F8A92]/30 transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-historical text-sm text-[#D4A84A]">{g.name}</p>
                  <p className="font-mono text-[10px] text-white/80 mt-0.5">{g.description}</p>
                  <p className="font-mono text-[9px] text-[#52525B] mt-1">
                    {(g.member_ids || []).length}/12 members · updated {new Date(g.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
